import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortDishInfo } from 'types';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortDishInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setDishes: (value: string[]) => void;
	selectedCustomer: Nullable<string>;
	selectedDishes: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedDishes,
	selectedCustomer,
	setDishes,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectOneDish') }], [t]);

	const filteredDataWithAdditionalContent = useMemo(() => {
		const updatedData = data.map((el) => ({
			...el,
			additionalContent: (
				<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
			),
		}));

		if (searchString) {
			const filteredDataByName = updatedData.filter((el) =>
				el.name.toLowerCase().includes(searchString.toLowerCase()),
			);
			const filteredDataByExternalId = updatedData.filter((el) =>
				el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
			);
			const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
			return unionFilteredData;
		}
		return updatedData;
	}, [searchString, data]);

	const handleSelectAllData = useCallback(() => {
		if (data.length === selectedDishes.length) {
			setDishes([]);
		} else {
			setDishes(data.map((el) => el.id));
		}
	}, [data, setDishes, selectedDishes]);

	useEffect(() => {
		setSearchString('');
	}, [selectedCustomer]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(data.length && data.length === selectedDishes.length)}
							disabled={!data.length}
							onChange={handleSelectAllData}>
							<Text translationNameSpace="form" translationKey="selectAllDishesLabel" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[data, selectedDishes, handleSelectAllData, setSearchString, searchString],
	);

	return (
		<FormSelect
			name="selectedDishes"
			label="dishesLabel"
			options={filteredDataWithAdditionalContent}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			onChange={setDishes}
			disabled={!selectedCustomer || isLoading}
			validationRules={validationRules}
			mode="multiple"
			counterValue={selectedDishes.length}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(MultipleSelect);
